import type { ReactNode } from 'react'
import { router, type ResolvedComponent } from '@inertiajs/react'
import { renderApp } from '@inertiaui/modal-react'
import AppProviders from '@frontend/pages/AppProviders'
import SidebarLayout from '@frontend/pages/SidebarLayout'
import { toastFlash } from '@stores/toastStore'
import { mountInertiaApp, type AppSetup } from './mountInertiaApp'

type PageComponent = ResolvedComponent & { layout?: (page: ReactNode) => ReactNode }

// modal-react's declared props predate Inertia v3's App; the runtime contract matches.
const renderModalApp = renderApp as unknown as (App: AppSetup['App'], props: AppSetup['props']) => ReactNode

export type ApplicationPages = Record<string, unknown>

export function resolveApplicationPage(pages: ApplicationPages, name: string): PageComponent {
  const page = (pages[`../pages/${name}.tsx`] ?? pages[`../contexts/${name}.tsx`]) as
    | { default: PageComponent }
    | undefined

  if (!page) {
    throw new Error(`Unknown page ${name}. Is it at app/frontend/pages/${name}.tsx?`)
  }

  const component = page.default
  component.layout ??= (pageElement) => <SidebarLayout>{pageElement}</SidebarLayout>

  return component
}

export function mountApplication({ pages }: { pages: ApplicationPages }) {
  return mountInertiaApp({
    title: (title) => (title ? `Supporter Base | ${title}` : 'Supporter Base'),
    resolve: (name) => resolveApplicationPage(pages, name),
    render: (App, props) => <AppProviders {...props}>{renderModalApp(App, props)}</AppProviders>,
    subscribe: () => router.on('flash', (event) => toastFlash(event.detail.flash)),
  })
}
